import { useCallback, useContext } from 'react';

import { PushContext } from '../PushProvider';

import { requestPushPermission } from './requestPushPermission';

/**
 * Games wrapper around {@link requestPushPermission}. Reads `updatePushStatus`
 * from {@link PushContext} so callers do not thread it through.
 *
 * Writes `called` before the SDK prompt. `granted` / `denied` come from
 * OneSignal `permissionChange`. If the call settles without a decision,
 * writes `default` so status cannot stay on `called`.
 *
 * Must run under `PushProvider`. Gambling uses {@link requestPushPermission}
 * directly (no push status store).
 *
 * @returns {() => Promise<NotificationPermission | undefined>}
 */
export const useRequestPushPermission = () => {
    const { updatePushStatus } = useContext(PushContext);

    return useCallback(async () => {
        updatePushStatus('called');

        const permission = await requestPushPermission();

        if (permission !== 'granted' && permission !== 'denied') {
            updatePushStatus('default');
        }

        return permission;
    }, [updatePushStatus]);
};
